<template>
  <Teleport to="body">
    <div
      v-show="visible"
      ref="popoverEl"
      class="fixed z-[9999] bg-white border border-gray-200 rounded-lg shadow-xl w-[480px]"
      :style="popoverStyle"
      @click.stop="closeInternalDropdowns"
    >
      <!-- Header -->
      <div class="flex items-center justify-between px-4 py-3 border-b border-gray-100">
        <span class="text-sm font-medium text-gray-900">Filters</span>
        <button @click="$emit('close')" class="text-gray-400 hover:text-gray-600">
          <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
        </button>
      </div>

      <!-- Filter rows -->
      <div v-if="filters.length" class="px-4 py-3 space-y-2">
        <div
          v-for="(filter, idx) in filters"
          :key="idx"
          class="flex items-center gap-2"
        >
          <!-- Field selector -->
          <select
            :value="filter.field"
            @change="updateFilter(idx, 'field', $event.target.value)"
            class="text-xs border border-gray-300 rounded px-2 py-1.5 w-[110px] bg-white"
          >
            <option
              v-for="f in fieldDefs"
              :key="f.key"
              :value="f.key"
              :disabled="f.key !== filter.field && isFieldMaxed(f.key)"
            >{{ f.label }}</option>
          </select>

          <!-- Operator selector -->
          <select
            :value="filter.operator"
            @change="updateFilter(idx, 'operator', $event.target.value)"
            class="text-xs border border-gray-300 rounded px-2 py-1.5 w-[80px] bg-white"
          >
            <option v-for="op in getOperators(filter.field)" :key="op.key" :value="op.key">{{ op.label }}</option>
          </select>

          <!-- Value: single calendar day -->
          <input
            v-if="kindOf(filter.field) === 'date'"
            type="date"
            :value="filter.value || ''"
            class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            @change="updateFilter(idx, 'value', $event.target.value || null)"
          />

          <!-- Value: single count -->
          <input
            v-else-if="kindOf(filter.field) === 'count'"
            type="number"
            min="0"
            step="1"
            :value="filter.value ?? ''"
            class="flex-1 text-xs border border-gray-300 rounded px-2 py-1.5 bg-white"
            @input="updateFilter(idx, 'value', $event.target.value === '' ? null : Number($event.target.value))"
          />

          <!-- Value: multi-select checkboxes -->
          <div v-else class="relative flex-1">
            <button
              @click.stop="toggleMultiDropdown(idx)"
              class="w-full text-xs border border-gray-300 rounded px-2 py-1.5 text-left bg-white truncate"
            >
              {{ multiDisplayText(filter) }}
            </button>
            <div
              v-if="openMultiIdx === idx"
              class="absolute top-full left-0 mt-1 z-[10000] bg-white border border-gray-200 rounded shadow-lg max-h-40 overflow-y-auto min-w-full"
              @click.stop
            >
              <label
                v-for="opt in getValueOptions(filter.field)"
                :key="opt.value"
                class="flex items-center gap-2 px-3 py-1.5 hover:bg-gray-50 cursor-pointer text-xs text-gray-700"
              >
                <input
                  type="checkbox"
                  :checked="Array.isArray(filter.value) && filter.value.includes(opt.value)"
                  @change="toggleMultiValue(idx, opt.value)"
                  class="rounded border-gray-300 text-primary-500 focus:ring-primary-500 h-3 w-3"
                />
                {{ opt.label }}
              </label>
            </div>
          </div>

          <!-- Remove row -->
          <button @click="removeFilter(idx)" class="text-gray-300 hover:text-gray-500 shrink-0">
            <FontAwesomeIcon :icon="['fas', 'times']" class="text-xs" />
          </button>
        </div>
      </div>

      <!-- Add filter (outside scrollable area so dropdown isn't clipped) -->
      <div class="relative px-4 py-2" ref="addFilterContainer">
        <button
          @click.stop="toggleAddMenu"
          class="text-xs text-primary-600 hover:text-primary-700 font-medium flex items-center gap-1 py-1"
        >
          <FontAwesomeIcon :icon="['fas', 'plus']" class="text-[10px]" />
          Add filter
        </button>
        <div
          v-if="addMenuOpen"
          class="absolute top-full left-4 mt-1 z-[10000] bg-white border border-gray-200 rounded shadow-lg py-1 min-w-[140px]"
          @click.stop
        >
          <button
            v-for="f in addMenuFields"
            :key="f.key"
            class="block w-full text-left text-xs px-3 py-1.5 hover:bg-gray-50 text-gray-700"
            @click="addFilter(f.key)"
          >
            {{ f.label }}
          </button>
        </div>
      </div>

      <!-- Hide completed -->
      <div class="px-4 py-2.5 border-t border-gray-100">
        <label class="flex items-center gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            :checked="hideCompleted"
            @change="$emit('update:hideCompleted', $event.target.checked)"
            class="rounded border-gray-300 text-primary-500 focus:ring-primary-500 h-3.5 w-3.5"
          />
          <span class="text-xs text-gray-600">Hide completed items</span>
        </label>
      </div>

      <!-- Clear all -->
      <div class="px-4 py-2.5 border-t border-gray-100">
        <button
          @click="clearAll"
          class="text-xs text-gray-400 hover:text-gray-600"
          :class="{ 'opacity-50 pointer-events-none': !filters.length && !hideCompleted }"
        >
          Clear all filters
        </button>
      </div>
    </div>
  </Teleport>
</template>

<script>
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { PRIORITY_CODES, PRIORITY_LABELS } from './support_constants';

// `kind` picks the operator vocabulary and the value editor:
//   set   -- any_of / none_of over a checkbox list (the original behaviour)
//   date  -- on_or_after / on_or_before against a single calendar day
//   count -- on_or_after / on_or_before against a single integer
//
// 'entity' is deliberately absent: it is populated on 1 of 362 open rows, so
// the dropdown would be empty for every practical purpose.
const FIELD_DEFS = [
  { key: 'type', label: 'Type' },
  { key: 'status', label: 'Status' },
  { key: 'owner', label: 'Owner' },
  { key: 'requester', label: 'Requester' },
  { key: 'priority', label: 'Priority' },
  { key: 'tenant', label: 'Tenant' },
  { key: 'tags', label: 'Tags' },
  { key: 'customer_facing', label: 'Customer Facing' },
  { key: 'source', label: 'Source' },
  { key: 'needs_triage', label: 'Needs Triage' },
  { key: 'owner_is_staff', label: 'Staff-owned' },
  { key: 'due_date', label: 'Due Date', kind: 'date' },
  { key: 'created_at', label: 'Created', kind: 'date' },
  { key: 'updated_at', label: 'Updated', kind: 'date' },
  { key: 'comment_count', label: 'Comments', kind: 'count' },
  { key: 'children_count', label: 'Children', kind: 'count' },
];

const OPERATORS = [
  { key: 'any_of', label: 'is' },
  { key: 'none_of', label: 'is not' },
];

// A range is two rows on the same field with different operators. The
// same-field-same-operator guard in updateFilter() still blocks the duplicate
// that would make one of them dead weight.
const DATE_OPERATORS = [
  { key: 'on_or_after', label: 'on or after' },
  { key: 'on_or_before', label: 'on or before' },
];

const COUNT_OPERATORS = [
  { key: 'on_or_after', label: 'at least' },
  { key: 'on_or_before', label: 'at most' },
];

const OPERATORS_BY_KIND = {
  set: OPERATORS,
  date: DATE_OPERATORS,
  count: COUNT_OPERATORS,
};

const YES_NO_OPTIONS = [
  { value: 'yes', label: 'Yes' },
  { value: 'no', label: 'No' },
];

function fieldKind(field) {
  return FIELD_DEFS.find((f) => f.key === field)?.kind || 'set';
}

function operatorsFor(field) {
  return OPERATORS_BY_KIND[fieldKind(field)];
}

// Priority filters use canonical p-codes to match what the backend stores
// (p0-p3/none). Legacy urgent/high/medium/low is gone (Item 1).
const PRIORITY_OPTIONS = PRIORITY_CODES.map((code) => ({
  value: code,
  label: PRIORITY_LABELS[code],
}));

// One merged list means one status vocabulary: the union of what the
// Requirements and Tickets tabs each offered, in workflow order. 'draft' leads
// because the list ships with a "Status is not Draft" row the user clears from
// here. 'resolved' stays out -- deprecated (Item 2).
const MERGED_STATUSES = [
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'approved', label: 'Approved' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'waiting', label: 'Waiting' },
  { value: 'customer_validating', label: 'Customer Validating' },
  { value: 'done', label: 'Done' },
  { value: 'closed', label: 'Closed' },
];

export default {
  name: 'Support_FilterPopover',
  components: { FontAwesomeIcon },
  props: {
    visible: { type: Boolean, default: false },
    modelValue: { type: Array, default: () => [] },
    hideCompleted: { type: Boolean, default: false },
    requirementTypes: { type: Array, default: () => [] },
    users: { type: Array, default: () => [] },
    tenantFilterOptions: { type: Array, default: () => [] },
    allTags: { type: Array, default: () => [] },
    sourceFilterOptions: { type: Array, default: () => [] },
    anchorRect: { type: Object, default: null },
  },
  emits: ['update:modelValue', 'update:hideCompleted', 'close'],
  data() {
    return {
      addMenuOpen: false,
      openMultiIdx: null,
    };
  },
  computed: {
    filters() {
      return this.modelValue;
    },
    fieldDefs() {
      return FIELD_DEFS;
    },
    addMenuFields() {
      return FIELD_DEFS.filter(f => !this.isFieldMaxed(f.key));
    },
    popoverStyle() {
      if (!this.anchorRect) return { top: '0px', left: '0px' };
      const popoverWidth = 480;
      let top = this.anchorRect.bottom + 4;
      let left = this.anchorRect.left;
      // Flip above anchor if near bottom of viewport
      if (top + 300 > window.innerHeight) {
        top = Math.max(8, this.anchorRect.top - 300 - 4);
      }
      // Shift left if near right edge of viewport
      if (left + popoverWidth > window.innerWidth) {
        left = Math.max(8, window.innerWidth - popoverWidth - 8);
      }
      return {
        top: `${top}px`,
        left: `${left}px`,
      };
    },
  },
  watch: {
    visible(val) {
      if (!val) {
        this.addMenuOpen = false;
        this.openMultiIdx = null;
      }
    },
  },
  mounted() {
    document.addEventListener('click', this.onDocClick);
    document.addEventListener('keydown', this.onKeydown);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.onDocClick);
    document.removeEventListener('keydown', this.onKeydown);
  },
  methods: {
    onDocClick(e) {
      if (!this.visible) return;
      const popover = this.$refs.popoverEl;
      if (popover && !popover.contains(e.target)) {
        this.$emit('close');
      }
    },
    onKeydown(e) {
      if (!this.visible) return;
      if (e.key !== 'Escape') return;
      e.stopPropagation();
      if (this.openMultiIdx !== null) {
        this.openMultiIdx = null;
      } else if (this.addMenuOpen) {
        this.addMenuOpen = false;
      } else {
        this.$emit('close');
      }
    },
    closeInternalDropdowns() {
      this.openMultiIdx = null;
      this.addMenuOpen = false;
    },
    getOperators(field) {
      return operatorsFor(field);
    },
    kindOf(field) {
      return fieldKind(field);
    },
    getValueOptions(field) {
      if (field === 'type') {
        return this.requirementTypes.map(t => ({ value: t.id, label: t.name }));
      }
      if (field === 'status') {
        return MERGED_STATUSES;
      }
      if (field === 'owner') {
        return [
          { value: '__none__', label: 'No owner' },
          ...this.users.map(u => ({ value: u.id, label: u.full_name })),
        ];
      }
      if (field === 'requester') {
        return [
          { value: '__none__', label: 'No requester' },
          ...this.users.map(u => ({ value: u.id, label: u.full_name })),
        ];
      }
      if (field === 'priority') {
        return PRIORITY_OPTIONS;
      }
      if (field === 'tenant') {
        return this.tenantFilterOptions;
      }
      if (field === 'tags') {
        return this.allTags.map(t => ({ value: t, label: t }));
      }
      if (field === 'customer_facing') {
        return [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No (Internal)' },
        ];
      }
      if (field === 'source') {
        return this.sourceFilterOptions;
      }
      if (field === 'needs_triage' || field === 'owner_is_staff') {
        return YES_NO_OPTIONS;
      }
      return [];
    },
    defaultValue(field) {
      return fieldKind(field) === 'set' ? [] : null;
    },
    defaultOperator(field) {
      return operatorsFor(field)[0].key;
    },
    addFilter(field) {
      const operators = operatorsFor(field);
      const existingOps = new Set(this.filters.filter(f => f.field === field).map(f => f.operator));
      let op = operators[0].key;
      if (existingOps.has(op)) {
        // The second row on a date/count field is the other half of a range.
        const alt = operators.find(o => !existingOps.has(o.key));
        if (!alt) { this.addMenuOpen = false; return; }
        op = alt.key;
      }
      const updated = [
        ...this.filters,
        { field, operator: op, value: this.defaultValue(field) },
      ];
      this.$emit('update:modelValue', updated);
      this.addMenuOpen = false;
    },
    isFieldMaxed(field) {
      const usedOps = this.filters.filter(f => f.field === field).map(f => f.operator);
      return operatorsFor(field).every(op => usedOps.includes(op.key));
    },
    removeFilter(idx) {
      this.openMultiIdx = null;
      const updated = this.filters.filter((_, i) => i !== idx);
      this.$emit('update:modelValue', updated);
    },
    updateFilter(idx, key, val) {
      const updated = this.filters.map((f, i) => {
        if (i !== idx) return f;
        const copy = { ...f, [key]: val };
        if (key === 'field') {
          // Reset value when field changes — a date row and a set row hold
          // different shapes, so the old value can never carry over.
          copy.value = this.defaultValue(val);
          // Check if default operator already used for new field
          const operators = operatorsFor(val);
          const existingOps = new Set(this.filters.filter((ff, ii) => ii !== idx && ff.field === val).map(ff => ff.operator));
          let op = operators[0].key;
          if (existingOps.has(op)) {
            const alt = operators.find(o => !existingOps.has(o.key));
            if (!alt) return f; // Can't switch — both operators used for target field
            op = alt.key;
          }
          copy.operator = op;
        }
        if (key === 'operator') {
          // Block if same field+operator already exists
          if (this.filters.some((ff, ii) => ii !== idx && ff.field === f.field && ff.operator === val)) return f;
        }
        return copy;
      });
      this.$emit('update:modelValue', updated);
    },
    toggleAddMenu() {
      this.addMenuOpen = !this.addMenuOpen;
      if (this.addMenuOpen) this.openMultiIdx = null;
    },
    toggleMultiDropdown(idx) {
      this.openMultiIdx = this.openMultiIdx === idx ? null : idx;
      this.addMenuOpen = false;
    },
    toggleMultiValue(idx, optValue) {
      const filter = this.filters[idx];
      const current = Array.isArray(filter.value) ? filter.value : [];
      const updated = current.includes(optValue)
        ? current.filter(v => v !== optValue)
        : [...current, optValue];
      this.updateFilter(idx, 'value', updated);
    },
    multiDisplayText(filter) {
      if (!Array.isArray(filter.value) || filter.value.length === 0) return 'Select...';
      const options = this.getValueOptions(filter.field);
      if (filter.value.length === 1) {
        const opt = options.find(o => o.value === filter.value[0]);
        return opt ? opt.label : '1 selected';
      }
      return `${filter.value.length} selected`;
    },
    clearAll() {
      this.$emit('update:modelValue', []);
      this.$emit('update:hideCompleted', false);
    },
    getFieldLabel(field) {
      const def = FIELD_DEFS.find(f => f.key === field);
      return def ? def.label : field;
    },
  },
};
</script>
